/**
 * A client's connected apps switch (docs/44_CONNECTED_APPS.md), apart from
 * API access: connecting an application Connect has approved is the client's
 * own decision in its portal and needs no API key (owner, 2026-11-02). The
 * server says which switches are this card's (`needs_api_access: false`); the
 * API access card shows the rest.
 */

import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2 } from 'lucide-react';
import {
  ApiError,
  Card,
  CardBody,
  CardHeader,
  QueryPending,
  ReadOnly,
  Switch,
  api,
  toast,
  useCan,
} from '@connect/web-shared';

import { messagingKeys } from './types';

interface Feature {
  name: string;
  label: string;
  description: string;
  enabled: boolean;
  needs_api_access?: boolean;
}

interface Settings {
  features: Feature[];
}

export function ClientConnectedAppsCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.integration.edit');
  const url = `/messaging/clients/${clientPublicId}/integrations`;
  // The API access card's own query: one request, and each card sees the
  // other's change at once.
  const key = messagingKeys.integrations(clientPublicId);
  const settings = useQuery({ queryKey: key, queryFn: () => api.get<Settings>(url) });
  const save = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      api.put<Settings>(`${url}/features/${name}`, { enabled }),
    onSuccess: (data, { enabled }) => {
      queryClient.setQueryData(key, data);
      toast.success(enabled ? `Connected apps on for ${clientName}` : `Connected apps off for ${clientName}`);
    },
    onError: (caught) => toast.error('Could not change it', caught instanceof ApiError ? caught.message : undefined),
  });
  const own = settings.data?.features.filter((f) => f.needs_api_access === false) ?? [];

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Link2 className="size-4" />
              Connected apps
            </span>
          }
          description={`Lets ${clientName} connect applications Connect has approved by signing in — no API key, and no API access needed.`}
        />
        <CardBody className="space-y-3">
          {settings.data === undefined ? (
            <QueryPending query={settings} label="Loading connected apps" />
          ) : (
            own.map((item) => (
              <Switch
                key={item.name}
                label={item.label}
                checked={item.enabled}
                disabled={save.isPending}
                hint={
                  item.enabled
                    ? `${clientName} sees and disconnects its applications under Connected apps in its app. Turning this off stops every connection at once.`
                    : item.description
                }
                onChange={(event) => save.mutate({ name: item.name, enabled: event.target.checked })}
              />
            ))
          )}
          <p className="text-xs text-muted">
            Which applications exist, and whether each is approved, is set under{' '}
            <Link className="text-accent hover:underline" to="/messaging/connected-apps">
              Connected apps
            </Link>
            .
          </p>
        </CardBody>
      </Card>
    </ReadOnly>
  );
}
